import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Lock, X } from "lucide-react";
import {
  DEMO_ACCESS_PASSWORD,
  DEMO_TRIAL_WHATSAPP_URL,
} from "../../../constants/demo-access";

export default function DemoAccessModal({ isOpen, onClose, demoLabel, onUnlock }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    setPassword("");
    setError("");
    document.body.classList.add("nav-menu-open");

    const timer = setTimeout(() => inputRef.current?.focus(), 50);

    const handleEscape = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleEscape);
    return () => {
      clearTimeout(timer);
      document.body.classList.remove("nav-menu-open");
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = (event) => {
    event.preventDefault();

    if (password === DEMO_ACCESS_PASSWORD) {
      onUnlock();
      return;
    }

    setError("Incorrect password. Get in touch for trial access.");
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-access-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close demo access dialog"
      />

      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="p-6 sm:p-8">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-teal/10 text-teal mb-5">
            <Lock size={22} aria-hidden />
          </div>

          <h2
            id="demo-access-title"
            className="text-xl font-bold text-navy text-center"
          >
            Try {demoLabel}
          </h2>
          <p className="mt-2 text-sm text-slate-500 text-center leading-relaxed">
            Enter the demo password once to unlock all live demos on this page.
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label
                htmlFor="demo-access-password"
                className="block text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-400 mb-2"
              >
                Demo Password
              </label>
              <input
                ref={inputRef}
                id="demo-access-password"
                type="password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  if (error) setError("");
                }}
                placeholder="Enter password"
                autoComplete="off"
                className="w-full px-4 py-3 rounded-lg border border-slate-200 text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal transition-colors"
              />
              {error && (
                <p className="mt-2 text-sm text-red-600" role="alert">
                  {error}
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-[6px] bg-teal text-white font-semibold text-[15px] transition-all duration-200 hover:bg-[#11997A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal"
            >
              Unlock &amp; Try Demo
              <span aria-hidden>→</span>
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            Don&apos;t have the password?{" "}
            <a
              href={DEMO_TRIAL_WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-teal hover:text-[#11997A] transition-colors"
            >
              Get in touch for trial
            </a>
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}
